import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { invokeApi } from '../lib/invokeApi';

/**
 * A pending team invitation addressed to the signed-in user.
 *
 * `token` builds the `/invitations/:token` link — the accept/decline surface.
 */
export interface MyTeamInvitation {
  invitationId: string;
  teamId: string;
  teamName: string;
  token: string;
  roleId: string;
  expiresAt: string;
}

interface UseMyTeamInvitationsResult {
  invitations: MyTeamInvitation[];
  count: number;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Pending team invitations awaiting the signed-in user's response.
 *
 * Distinct from `useTeamInvitations(teamId)`, which lists invitations a team
 * has *sent* (admin-only via RLS). This one answers "has anyone invited me?" —
 * unreadable over PostgREST, so it goes through `/api/list-my-invitations`.
 */
export function useMyTeamInvitations(): UseMyTeamInvitationsResult {
  const { user } = useAuth();
  // Depend on the id, not the user object — a new object identity on each
  // render would re-create the callback and re-fire the effect in a loop.
  const userId = user?.id;
  const [invitations, setInvitations] = useState<MyTeamInvitation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchInvitations = useCallback(async () => {
    if (!userId) {
      setInvitations([]);
      setError(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error: fnError } = await invokeApi<{
        success: boolean;
        data: MyTeamInvitation[];
        error?: string;
      }>('list-my-invitations');

      if (fnError) throw fnError;
      if (data && !data.success) throw new Error(data.error || 'Failed to load invitations');

      setInvitations(data?.data ?? []);
    } catch (err: any) {
      // A failed lookup must not break the menu it lives in — surface the
      // message for callers that want it and leave the count at zero.
      console.error('Error fetching my team invitations:', err);
      setError(err.message || 'Failed to load invitations');
      setInvitations([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchInvitations();
  }, [fetchInvitations]);

  return useMemo(() => ({
    invitations,
    count: invitations.length,
    loading,
    error,
    refetch: fetchInvitations,
  }), [invitations, loading, error, fetchInvitations]);
}
